import React from 'react';

import { AchievementMenu } from './AchievementMenu';
import type { Achievement } from './achievements/types';
import { BuildingStatusMenu } from './BuildingMenu';
import { BuildMenu } from './Buildings';
import { HandCraftingStatus } from './components/HandCraftingStatus';
import { LoadingScreen } from './components/LoadingScreen';
import {
  SpaceCivMode,
  calculateInitialGalaxyState,
} from './components/SpaceCivMode';
import { StartMenu } from './components/StartMenu';
import { ThreeRenderer } from './components/ThreeRenderer';
import { ToastContainer } from './components/Toast';
import {
  SAVE_CONFIG,
  Recipe,
  ResearchId,
  RESEARCHES,
  CONTROLS,
  formatHotkeyLabel,
} from './constants';
import { useUIState, useMapState } from './contexts/GameStateManager';
import { DEMO_SLOT_INDEX, getDemoSave } from './DemoSave';
import { GameBoard } from './GameBoard';
import {
  setAssemblerRecipe,
  getAssemblerInventory,
  getChestInventory,
  getBuildingAtPosition,
} from './GameState';
import { useDebugState, DebugState } from './hooks/useDebugState';
import { useToast } from './hooks/useToast';
import { useKeyboardInput } from './Input';
import LeftPanel from './LeftPanel';
import { saveLogger } from './Logger';
import PlayerInventoryWindow from './PlayerInventory';
import { ProductionMenu } from './ProductionMenu';
import { ResearchMenu } from './ResearchMenu';
import {
  getRequiredResearchForRecipe,
  isResearchRepeatable,
  getRepeatableResearchLevel,
} from './ResearchSystem';
import ResourceNodeMenu from './ResourceNodeMenu';
import { SaveMenu } from './SaveMenu';
import TopMenu from './TopMenu';
import {
  serializeSlotData,
  parseStoredSlotData,
  isSaveData,
  getSaveOverview,
  SaveOverview,
} from './utils/saveSlots';

interface FactoryModeLayerProps {
  uiState: ReturnType<typeof useUIState>;
  mapState: ReturnType<typeof useMapState>;
  debugState: DebugState;
  setDebugState: (
    state: DebugState | ((prevState: DebugState) => DebugState)
  ) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  inventoryHotkeyLabel: string;
}

const FactoryModeLayer: React.FC<FactoryModeLayerProps> = ({
  uiState,
  mapState,
  debugState,
  setDebugState,
  showSuccess,
  showError,
  inventoryHotkeyLabel,
}) => {
  const hasPrestiged =
    (mapState.prestigeCount ?? 0) > 0 || (mapState.prestigePoints ?? 0) > 0;
  const { prestigeMenuOpen, setPrestigeMenuOpen } = uiState;
  const { rendererMode } = mapState;

  React.useEffect(() => {
    if (!hasPrestiged && prestigeMenuOpen) {
      setPrestigeMenuOpen(false);
    }
  }, [hasPrestiged, prestigeMenuOpen, setPrestigeMenuOpen]);

  // Handle keyboard input with real UI state from context
  useKeyboardInput({
    gameState: {
      saveGame: mapState.saveGame,
    },
    buildMenuOpen: uiState.buildMenuOpen !== false,
    setBuildMenuOpen: uiState.setBuildMenuOpen,
    setSelectedCategory: uiState.setSelectedCategory,
    setSelectedBuilding: uiState.setSelectedBuilding,
    setInventoryOpen: uiState.setInventoryOpen,
  });

  const handleRecipeSelect = (recipe: Recipe | null) => {
    const target = uiState.buildingStatusMenuTarget;
    if (target) {
      if (recipe) {
        const requiredResearchId = getRequiredResearchForRecipe(recipe.id);
        if (
          requiredResearchId &&
          !mapState.isResearchUnlocked(requiredResearchId)
        ) {
          const researchName =
            RESEARCHES[requiredResearchId]?.name ?? 'the required tech';
          showError(`${recipe.name} requires ${researchName}`);
          return;
        }
      }

      mapState.setAssemblerInventoryLayer(prevLayer => {
        return setAssemblerRecipe(
          prevLayer,
          mapState.buildingLayer,
          target,
          recipe,
          mapState.researchState
        );
      });

      if (recipe) {
        showSuccess(`Selected recipe: ${recipe.name}`);
      } else {
        showSuccess('Recipe cleared - assembler ready for new recipe');
      }
    }
  };

  return (
    <>
      <div className='game-main'>
        {rendererMode === 'three' ? <ThreeRenderer /> : <GameBoard />}
        <HandCraftingStatus queue={mapState.handCraftingQueue} />
      </div>

      <LeftPanel debugState={debugState} setDebugState={setDebugState} />

      <BuildMenu
        buildMenuOpen={uiState.buildMenuOpen}
        selectedCategory={uiState.selectedCategory}
        selectedBuilding={uiState.selectedBuilding}
      />

      {uiState.inventoryOpen && (
        <PlayerInventoryWindow
          inventory={mapState.playerInventory}
          updateInventory={mapState.updatePlayerInventory}
          onClose={() => uiState.setInventoryOpen(false)}
          hotkeyLabel={inventoryHotkeyLabel}
          handCraftingQueue={mapState.handCraftingQueue}
          onQueueHandCrafting={mapState.queueHandCrafting}
          researchState={mapState.researchState}
        />
      )}

      <ResearchMenu
        isOpen={uiState.researchMenuOpen}
        researchState={mapState.researchState}
        onSelect={researchId => mapState.selectResearch(researchId)}
        onClearSelection={() => mapState.selectResearch(null)}
        onUnlock={researchId => {
          const repeatable = isResearchRepeatable(researchId);
          const alreadyUnlocked = mapState.isResearchUnlocked(researchId);
          if (!repeatable && alreadyUnlocked) {
            return;
          }

          const previousLevel = repeatable
            ? getRepeatableResearchLevel(mapState.researchState, researchId)
            : 0;

          mapState.unlockResearch(researchId);

          const researchName = RESEARCHES[researchId]?.name ?? 'research';
          if (repeatable) {
            const nextLevel = previousLevel + 1;
            showSuccess(`Upgraded ${researchName} to level ${nextLevel}`);
          } else {
            showSuccess(`Unlocked ${researchName}`);
          }
        }}
        {...(mapState.hasEnabledDebug
          ? {
              onForceUnlock: (researchId: ResearchId) => {
                mapState.forceUnlockResearch(researchId);
                const researchName = RESEARCHES[researchId]?.name ?? 'research';
                showSuccess(`Force unlocked ${researchName}`);
              },
            }
          : {})}
        debugModeEnabled={mapState.hasEnabledDebug}
        onClose={() => uiState.setResearchMenuOpen(false)}
      />
      <ResearchMenu
        variant='prestige'
        isOpen={uiState.prestigeMenuOpen}
        prestigeState={mapState.prestigeUpgradeState}
        prestigePoints={mapState.prestigePoints ?? 0}
        onPrestigeSelect={mapState.selectPrestigeUpgrade}
        onPrestigePurchase={mapState.purchasePrestigeUpgrade}
        onClose={() => uiState.setPrestigeMenuOpen(false)}
      />
      <ProductionMenu
        isOpen={uiState.productionMenuOpen}
        history={mapState.itemProductionHistory}
        currentTick={mapState.currentTick}
        onClose={() => uiState.setProductionMenuOpen(false)}
      />

      {uiState.achievementMenuOpen && (
        <AchievementMenu
          achievements={mapState.achievements}
          onClose={() => uiState.setAchievementMenuOpen(false)}
        />
      )}

      {uiState.buildingStatusMenuTarget &&
        (() => {
          const { building } = getBuildingAtPosition(
            uiState.buildingStatusMenuTarget.x,
            uiState.buildingStatusMenuTarget.y,
            mapState.buildingLayer
          );

          if (!building) {
            return null;
          }

          const assemblerInventory = getAssemblerInventory(
            mapState.assemblerInventoryLayer,
            mapState.buildingLayer,
            uiState.buildingStatusMenuTarget
          );
          const chestInventory = getChestInventory(
            mapState.chestInventoryLayer,
            mapState.buildingLayer,
            uiState.buildingStatusMenuTarget
          );
          const currentRecipe = assemblerInventory?.recipe || null;
          const inventoryCounts = assemblerInventory?.items || {};
          const craftingProgress = assemblerInventory?.craftingProgress || 0;
          const craftingStartTick =
            assemblerInventory?.craftingStartTick ?? null;

          return (
            <BuildingStatusMenu
              building={building}
              position={uiState.buildingStatusMenuTarget}
              onRecipeSelect={handleRecipeSelect}
              selectedRecipe={currentRecipe}
              inventoryCounts={inventoryCounts}
              craftingProgress={craftingProgress}
              craftingStartTick={craftingStartTick}
              minerTimerLayer={mapState.minerTimerLayer}
              minerOutputBufferLayer={mapState.minerOutputBufferLayer}
              chestInventory={chestInventory}
              stackCapacity={building.stackCapacity ?? 0}
            />
          );
        })()}
      <ResourceNodeMenu />
    </>
  );
};

export const AppContent: React.FC = () => {
  // Use contexts for state management
  const uiState = useUIState();
  const mapState = useMapState();
  const { debugState, setDebugState, toggleDebugDetails } = useDebugState();
  const { toasts, showSuccess, showError, removeToast } = useToast();
  const [saveOverview, setSaveOverview] = React.useState<SaveOverview>({
    hasAnySave: false,
    latestSaveName: null,
    latestSaveTimestamp: null,
  });
  const hasInitializedAchievementsRef = React.useRef(false);
  const unlockedAchievementIdsRef = React.useRef<Set<Achievement['id']>>(
    new Set()
  );

  React.useEffect(() => {
    const unlockedAchievements = mapState.achievements.filter(
      achievement => achievement.unlocked
    );
    const currentUnlockedIds = new Set(
      unlockedAchievements.map(achievement => achievement.id)
    );

    if (!hasInitializedAchievementsRef.current) {
      unlockedAchievementIdsRef.current = currentUnlockedIds;
      hasInitializedAchievementsRef.current = true;
      return;
    }

    const previouslyUnlockedIds = unlockedAchievementIdsRef.current;

    unlockedAchievements.forEach(achievement => {
      if (!previouslyUnlockedIds.has(achievement.id)) {
        const icon = achievement.icon || '🏆';
        showSuccess(`${icon} Achievement unlocked: ${achievement.name}`, 5000);
      }
    });

    unlockedAchievementIdsRef.current = currentUnlockedIds;
  }, [mapState.achievements, showSuccess]);

  const refreshSaveOverview = React.useCallback(() => {
    setSaveOverview(getSaveOverview());
  }, []);

  const isLoading = mapState.isLoading;

  React.useEffect(() => {
    if (!isLoading) {
      refreshSaveOverview();
    }
  }, [isLoading, refreshSaveOverview]);

  const inventoryHotkeyLabel = formatHotkeyLabel(CONTROLS.INVENTORY);
  const isSpaceModeUnlocked = (mapState.prestigePoints ?? 0) >= 1000;

  const handleSaveToSlot = (saveKey: string, displayName: string): boolean => {
    // Check if this is the demo slot - extract slot index from saveKey
    const slotMatch = saveKey.match(/_slot_(\d+)$/);
    if (slotMatch && slotMatch[1]) {
      const slotIndex = parseInt(slotMatch[1], 10);
      if (slotIndex === DEMO_SLOT_INDEX) {
        showError('Cannot save to the demo slot. Please use a different slot.');
        return false;
      }
    }

    try {
      const didSave = mapState.saveGame();
      if (!didSave) {
        showError('Failed to save the current game state.');
        return false;
      }

      const baseSave = localStorage.getItem(SAVE_CONFIG.SAVE_KEY);
      if (!baseSave) {
        throw new Error('Base save data not found after saving.');
      }

      const parsedBase = JSON.parse(baseSave);
      if (!isSaveData(parsedBase)) {
        throw new Error('Base save data is invalid.');
      }

      const slotPayload = serializeSlotData(displayName, parsedBase);
      localStorage.setItem(saveKey, slotPayload);
      refreshSaveOverview();
      return true;
    } catch (error) {
      saveLogger.error('Failed to save to slot', { saveKey, error });
      showError('Failed to save to the selected slot.');
      return false;
    }
  };

  const handleLoadFromSlot = (saveKey: string): void => {
    try {
      const savedData = localStorage.getItem(saveKey);
      if (!savedData) {
        return;
      }

      const slotData = parseStoredSlotData(savedData);
      mapState.loadGameFromSaveData(slotData.saveData);
      localStorage.setItem(
        SAVE_CONFIG.SAVE_KEY,
        JSON.stringify(slotData.saveData)
      );
      uiState.setSaveMenuOpen(false);
      uiState.setStartMenuOpen(false);
      showSuccess(
        `Loaded ${slotData.name ? `"${slotData.name}"` : 'saved game'} successfully.`
      );
      refreshSaveOverview();
    } catch (error) {
      saveLogger.error('Failed to load from slot', { saveKey, error });
      showError('Failed to load the selected save slot.');
    }
  };

  const handleNewGame = React.useCallback((): boolean => {
    const shouldConfirm = saveOverview.hasAnySave;
    const confirmed =
      !shouldConfirm ||
      window.confirm(
        'Are you sure you want to start a new game? All unsaved progress will be lost.'
      );

    if (!confirmed) {
      return false;
    }

    mapState.resetGame();
    mapState.setSpaceGalaxyState(calculateInitialGalaxyState());
    uiState.setSaveMenuOpen(false);
    refreshSaveOverview();
    return true;
  }, [mapState, refreshSaveOverview, saveOverview.hasAnySave, uiState]);

  const handleStartMenuContinue = React.useCallback(() => {
    uiState.setStartMenuOpen(false);
  }, [uiState]);

  const handleStartMenuNewGame = React.useCallback(() => {
    const started = handleNewGame();
    if (started) {
      uiState.setStartMenuOpen(false);
      showSuccess('Started a new factory. Good luck, engineer!');
    }
  }, [handleNewGame, showSuccess, uiState]);

  const handleStartMenuLoadDemo = React.useCallback(() => {
    const demoSave = getDemoSave();
    if (!demoSave) {
      showError('Demo save is not available at the moment.');
      return;
    }

    try {
      const slotData = parseStoredSlotData(demoSave);
      mapState.loadGameFromSaveData(slotData.saveData);
      localStorage.setItem(
        SAVE_CONFIG.SAVE_KEY,
        JSON.stringify(slotData.saveData)
      );
      uiState.setSaveMenuOpen(false);
      uiState.setStartMenuOpen(false);
      showSuccess('Loaded the demonstration factory. Explore and enjoy!');
      refreshSaveOverview();
    } catch (error) {
      saveLogger.error('Failed to load demo save from start menu', { error });
      showError('Failed to load the demonstration save.');
    }
  }, [mapState, refreshSaveOverview, showError, showSuccess, uiState]);

  const handleStartMenuManageSaves = React.useCallback(() => {
    uiState.setStartMenuOpen(false);
    uiState.setSaveMenuOpen(true);
  }, [uiState]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className='App'>
      <TopMenu
        debugState={debugState}
        toggleDebugDetails={toggleDebugDetails}
      />

      <StartMenu
        isOpen={uiState.startMenuOpen}
        saveOverview={saveOverview}
        onStartNew={handleStartMenuNewGame}
        onContinue={handleStartMenuContinue}
        onLoadDemo={handleStartMenuLoadDemo}
        onManageSaves={handleStartMenuManageSaves}
      />
      {isSpaceModeUnlocked ? (
        <SpaceCivMode
          prestigePoints={mapState.prestigePoints}
          prestigeCount={mapState.prestigeCount}
          showSuccess={showSuccess}
          showError={showError}
          galaxyState={mapState.spaceGalaxyState}
          setGalaxyState={mapState.setSpaceGalaxyState}
        />
      ) : (
        <FactoryModeLayer
          uiState={uiState}
          mapState={mapState}
          debugState={debugState}
          setDebugState={setDebugState}
          showSuccess={showSuccess}
          showError={showError}
          inventoryHotkeyLabel={inventoryHotkeyLabel}
        />
      )}

      <SaveMenu
        saveMenuOpen={uiState.saveMenuOpen}
        onClose={() => uiState.setSaveMenuOpen(false)}
        onSave={handleSaveToSlot}
        onLoad={handleLoadFromSlot}
        onNewGame={handleNewGame}
        triggerAchievementCheck={mapState.triggerAchievementCheck}
      />

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};
